import { Controller } from '@hotwired/stimulus'

const VISIBLE = 'rnk-BarraCtaMovil-visible'
const CON_BARRA = 'rnk-con-BarraCtaMovil'
const ALTO = '--rnk-barra-cta-movil-alto'
const SIN_LOGO = 'rnk-BarraCtaMovil-sinLogo'
const PARTIDA = 'rnk-BarraCtaMovil-partida'
const SIEMPRE = 'rnk-BarraCtaMovil-siempre'

/*
 * Barra inferior fija de la ficha en móvil (propuesta B2): sale cuando el CTA
 * del hero ya no está a la vista y se va cuando vuelve.
 *
 * Se observa el hero y no la posición del scroll: un listener de scroll
 * recalcularía en cada píxel. Y se observa el hero entero y no su CTA porque
 * `sticky-header.js` se lleva el CTA a la cabecera fija al bajar, y desde ahí
 * siempre estaría «a la vista». El hero, en cambio, conserva su alto aunque la
 * cabecera se fije (el script se lo reserva al padre).
 *
 * Mientras el hero está atrás, el <body> lleva `rnk-con-BarraCtaMovil`: la hoja
 * de la barra reserva con eso su alto al pie y quita el CTA de la cabecera fija.
 *
 * Y mientras el CTA de cierre de la ficha (`.rnk-CtaCierre`, B4) está a la vista,
 * la barra se aparta: ese bloque ya lleva el mismo CTA y la barra lo taparía al
 * llegar a él por abajo. La marca del <body> se queda, para que el CTA no vuelva
 * a la cabecera fija y salga dos veces.
 *
 * Lo que se observa es `.rnk-Hero` salvo que la página diga otra cosa con
 * `data-barra-cta-movil-observa-value`: la plantilla `mejores` la saca al dejar
 * atrás el bloque «Broker del mes» (PRD133-1, ronda 20).
 *
 * Con `data-barra-cta-movil-siempre-value` la barra está siempre, de arriba
 * abajo de la página, sin mirar lo observado (`mejores` en móvil, Laura
 * 2026-10-27: «vamos a dejarlo siempre y ya está»). Lo marca
 * `rnk-BarraCtaMovil-siempre`, aparte de `-visible`, porque solo cuenta en
 * móvil: en desktop la barra sigue saliendo al pasar lo observado.
 */
export default class extends Controller {
  static values = {
    observa: { type: String, default: '.rnk-Hero' },
    siempre: { type: Boolean, default: false }
  }

  connect () {
    this.heroAtras = false
    this.cierreALaVista = false
    this.siempre = this.siempreValue
    this.observadores = []
    if (this.siempre) this.pinta()

    const hero = document.querySelector(this.observaValue)
    if (!hero) return

    this.observa(hero, { rootMargin: `-${this.margenSuperior(hero)}px 0px 0px 0px` }, entrada => {
      this.heroAtras = !entrada.isIntersecting && entrada.boundingClientRect.top < 0
    })

    const cierre = document.querySelector('.rnk-CtaCierre')
    if (cierre) {
      this.observa(cierre, {}, entrada => { this.cierreALaVista = entrada.isIntersecting })
    }
  }

  disconnect () {
    this.observadores.forEach(observador => observador.disconnect())
    this.observadores = []
    this.heroAtras = false
    this.cierreALaVista = false
    this.siempre = false
    this.pinta()
  }

  observa (elemento, opciones, anota) {
    const observador = new IntersectionObserver(([entrada]) => {
      anota(entrada)
      this.pinta()
    }, { threshold: 0, ...opciones })
    observador.observe(elemento)
    this.observadores.push(observador)
  }

  pinta () {
    const visible = this.heroAtras && !this.cierreALaVista
    this.element.classList.toggle(VISIBLE, visible)
    this.element.classList.toggle(SIEMPRE, this.siempre)
    const conBarra = this.heroAtras || this.siempre
    document.body.classList.toggle(CON_BARRA, conBarra)
    if (visible || this.siempre) this.reparte()
    if (conBarra) {
      document.body.style.setProperty(ALTO, `${this.element.offsetHeight}px`)
    } else {
      document.body.style.removeProperty(ALTO)
    }
  }

  /*
   * El CTA no se corta nunca y el dato cede antes (lo resuelve la hoja), pero
   * con un CTA largo en una pantalla estrecha el botón acaba bajando de fila.
   * Antes de eso sobra el logo; y si ni así cabe, el botón va a su fila a todo el
   * ancho y el logo vuelve, que arriba ya hay sitio. Se mide cada vez que la
   * barra sale, por si ha cambiado el ancho.
   */
  reparte () {
    const lista = this.element.classList
    lista.remove(SIN_LOGO, PARTIDA)
    if (!this.botonPartido()) return

    lista.add(SIN_LOGO)
    if (!this.botonPartido()) return

    lista.remove(SIN_LOGO)
    lista.add(PARTIDA)
  }

  botonPartido () {
    const producto = this.element.querySelector('.rnk-BarraCtaMovil_Producto')
    const boton = this.element.querySelector('.rnk-BarraCtaMovil_Boton')
    if (!producto || !boton) return false

    return boton.getBoundingClientRect().top >= producto.getBoundingClientRect().bottom
  }

  /*
   * Cuánto tiene que subir el borde inferior del hero para que su CTA quede
   * fuera: lo que hay entre el CTA y el final del hero (las pestañas) más el
   * menú del sitio, que va fijo arriba y lo tapa antes de que salga de la
   * ventana. Se mide al conectar, con la cabecera aún en su sitio; si ya está
   * fijada (se vuelve a una ficha a media página), basta con el menú.
   */
  margenSuperior (hero) {
    const menu = document.querySelector('.rnk-MainSiteNav')
    const altoMenu = menu ? menu.getBoundingClientRect().height : 0

    const cta = hero.querySelector('.rnk-Hero_CtaWrapper')
    const fijada = hero.querySelector('.rnk-Hero_HeaderWrapper-fixed')
    if (!cta || fijada) return Math.round(altoMenu)

    const bajoElCta = hero.getBoundingClientRect().bottom - cta.getBoundingClientRect().bottom
    return Math.round(Math.max(bajoElCta, 0) + altoMenu)
  }
}
